import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import ListFilters from '@/common/components/ListFilters'
import ListHeader from '@/common/components/ListHeader'
import Table from '@/common/components/Table'
import TextField from '@/common/components/TextField'
import { DatePickerWithRange } from '@/common/components/ui/dateRangePicker'
import useDebounce from '@/common/hooks/useDebounce'
import { useFilterDate } from '@/common/hooks/useFilterDate'
import { useFilterParams } from '@/common/hooks/useFilterParams'
import { Mapper } from '@/common/services/mapper'
import { defaultColumns } from '@/features/paymentAdvances/constants/columns'
import { paymentAdvanceStatusOptions } from '@/features/paymentAdvances/constants/paymentAdvanceStatus.constant'
import { usePaymentAdvances } from '@/features/paymentAdvances/hooks/usePaymentAdvances'
import { PaymentAdvancesTransformer } from '@/features/paymentAdvances/services/paymentAdvancesTransformer'
import { PaymentAdvanceData, PaymentAdvanceTableItem } from '@/features/paymentAdvances/types/paymentAdvance.type'
import { ArchiveIcon, SearchIcon } from 'lucide-react'

const PaymentAdvanceList = () => {
  const { filterParams, setFilterParams } = useFilterParams()
  const { date, formattedDate } = useFilterDate(filterParams)

  const [search, setSearch] = useDebounce(filterParams.search || '')

  const {
    data: paymentAdvances,
    meta,
    isLoading: isLoadingPaymentAdvances,
    error: paymentAdvancesError = {} as any,
    refetch: refetchPaymentAdvances,
  } = usePaymentAdvances<PaymentAdvanceTableItem[]>({
    filters: {
      page: filterParams.page,
      search: search,
      status: filterParams.status?.value,
      createdAtStartDate: formattedDate.from,
      createdAtEndDate: formattedDate.to,
    },
    mapper: Mapper.mapWithTransformer<PaymentAdvanceData[], PaymentAdvanceTableItem[]>({
      transformer: PaymentAdvancesTransformer.tableTransformer(),
    }),
  })

  return (
    <div className="flex flex-col gap-6">
      <ListHeader Icon={<ArchiveIcon className="size-10" strokeWidth={1} />} title="Antecipáveis de Fatura" />

      <ErrorMessageWrapper
        className="h-[calc(100vh-128px)]"
        statusCode={paymentAdvancesError?.status}
        refetchFunction={refetchPaymentAdvances}
      >
        <div className="flex flex-col gap-6">
          <TextField
            LeftIcon={<SearchIcon />}
            placeholder="Pesquisar"
            className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
            onChange={(e) => {
              setSearch((e.target as HTMLInputElement).value)
              setFilterParams((prev) => ({ ...prev, search: (e.target as HTMLInputElement).value }))
            }}
            value={filterParams.search}
          />

          <div className="flex items-center gap-2">
            <DatePickerWithRange date={date} setDate={(date) => setFilterParams((params) => ({ ...params, date }))} />

            <ListFilters
              filters={[
                {
                  className: 'w-40',
                  onChange: (status) => setFilterParams((prev) => ({ ...prev, status })),
                  value: filterParams.status,
                  options: [{ label: 'Todos', value: '' }, ...(paymentAdvanceStatusOptions || [])],
                  placeholder: 'Status',
                },
              ]}
            />
          </div>
        </div>

        <Table
          className="max-h-[calc(100vh-260px)]"
          columns={defaultColumns}
          isLoadingData={isLoadingPaymentAdvances}
          rows={paymentAdvances || []}
          totalPages={meta?.total_pages}
          totalCount={meta?.total}
          hasPagination
          rowEmptyValue="--"
          page={filterParams.page ? Number(filterParams.page) : 1}
          setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
        />
      </ErrorMessageWrapper>
    </div>
  )
}

export default PaymentAdvanceList
